import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { AHeader, ACard, ATable, inputDark } from "@/components/shared/AdminKit";
import { fmtDate } from "@/components/shared/DashKit";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Search, Send, X, Check, Ban, Plus, ShieldCheck } from "lucide-react";

const REQ_LABEL = { pending: "En attente", in_progress: "En cours", verification_required: "Vérification requise", approved: "Approuvée", rejected: "Refusée" };
const TYPE_LABEL = { add: "Ajout", remove: "Suppression", replace: "Modification / remplacement", verify: "Vérification du numéro principal" };
const NUM_LABEL = { verified: "Vérifié", unverified: "Non vérifié", removed: "Retiré", replaced: "Remplacé" };
const sbtn = "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50";

async function act(fn, ok, done) {
  try { await fn(); toast.success(ok); done && done(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
}

const fmtNum = (m) => (m ? `+${m}` : "—");

function OtpVerify({ r, onDone }) {
  const [code, setCode] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [busy, setBusy] = useState(false);
  const needEmail = r.type === "replace" && r.target_is_primary;
  const digits = (v) => v.replace(/\D/g, "").slice(0, 6);
  const submit = async () => {
    setBusy(true);
    await act(() => api.post(`/admin/payment-number-requests/${r.id}/verify-otp`, { code, email_code: needEmail ? emailCode : undefined }), "Code OTP validé", () => { setCode(""); setEmailCode(""); onDone(); });
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid={`pnr-verify-box-${r.id}`}>
      <input value={code} onChange={(e) => setCode(digits(e.target.value))} inputMode="numeric" autoComplete="off" placeholder="Code SMS du client" data-testid={`pnr-code-${r.id}`} className={`${inputDark} !w-36 !py-1 font-mono text-sm tracking-widest`} />
      {needEmail && <input value={emailCode} onChange={(e) => setEmailCode(digits(e.target.value))} inputMode="numeric" autoComplete="off" placeholder="Code e-mail du client" data-testid={`pnr-email-code-${r.id}`} className={`${inputDark} !w-36 !py-1 font-mono text-sm tracking-widest`} />}
      <button onClick={submit} disabled={busy || code.length !== 6 || (needEmail && emailCode.length !== 6)} data-testid={`pnr-verify-${r.id}`} className={`${sbtn} bg-emerald-600 text-white hover:opacity-90`}><ShieldCheck className="h-3 w-3" />Vérifier le code OTP</button>
    </div>
  );
}

function ConfirmRemoval({ r, onDone }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`pnr-remove-${r.id}`} className={`${sbtn} bg-emerald-600 text-white hover:opacity-90`}><Check className="h-3 w-3" />Confirmer la suppression</button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid={`pnr-remove-dialog-${r.id}`}>
          <AlertDialogHeader><AlertDialogTitle>Supprimer le numéro {fmtNum(r.target_msisdn)} ?</AlertDialogTitle>
            <AlertDialogDescription>Client : {r.user_email}. Motif : {r.reason || "—"}. Le numéro ne pourra plus être utilisé pour les paiements ; l'opération est conservée dans l'historique.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`pnr-remove-cancel-${r.id}`}>Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid={`pnr-remove-confirm-${r.id}`} className="bg-red-600 hover:bg-red-700" onClick={() => act(() => api.post(`/admin/payment-number-requests/${r.id}/approve-removal`), "Numéro supprimé", onDone)}>Confirmer la suppression</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function RequestActions({ r, onDone }) {
  const [reason, setReason] = useState("");
  const open = ["pending", "in_progress", "verification_required"].includes(r.status);
  if (!open) return <span className="text-xs text-slate-500">{r.status_reason || "—"}</span>;
  const post = (path, body, ok) => act(() => api.post(`/admin/payment-number-requests/${r.id}/${path}`, body), ok, onDone);
  const otpDone = !!r.otp_verified_at;
  const awaitingCode = r.status === "verification_required" && !otpDone;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-1">
        {r.status === "pending" && <button onClick={() => post("status", { status: "in_progress" }, "Demande prise en charge")} data-testid={`pnr-progress-${r.id}`} className={`${sbtn} bg-slate-700 text-white hover:bg-slate-600`}>Prendre en charge</button>}
        {r.type !== "remove" && !otpDone && <button onClick={() => post("start-verification", {}, "Code OTP envoyé par SMS au numéro concerné")} data-testid={`pnr-otp-${r.id}`} className={`${sbtn} bg-accent text-white hover:opacity-90`}><Send className="h-3 w-3" />{awaitingCode ? "Renvoyer le code OTP" : "Envoyer le code OTP"}</button>}
        {otpDone && <button onClick={() => post("approve", {}, r.type === "replace" ? "Remplacement confirmé" : "Demande approuvée, numéro ajouté")} data-testid={`pnr-approve-${r.id}`} className={`${sbtn} bg-emerald-600 text-white hover:opacity-90`}><Check className="h-3 w-3" />{r.type === "replace" ? "Confirmer le remplacement" : "Approuver"}</button>}
        {r.type === "remove" && <ConfirmRemoval r={r} onDone={onDone} />}
      </div>
      {awaitingCode && <OtpVerify r={r} onDone={onDone} />}
      <div className="flex flex-wrap items-center gap-1">
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motif du refus (obligatoire)" data-testid={`pnr-reason-${r.id}`} className={`${inputDark} !w-44 !py-1 text-xs`} />
        <button onClick={() => post("status", { status: "rejected", reason }, "Demande refusée")} disabled={!reason.trim()} data-testid={`pnr-reject-${r.id}`} className={`${sbtn} bg-red-600 text-white hover:opacity-90`}><Ban className="h-3 w-3" />Refuser</button>
      </div>
    </div>
  );
}

function NumberCell({ r }) {
  const op = r.operator_name && <p className="text-[11px] text-slate-400" data-testid={`pnr-operator-${r.id}`}>Opérateur : {r.operator_name}</p>;
  if (r.type === "replace") return <div><span className="font-mono text-xs">{fmtNum(r.target_msisdn)}{r.target_is_primary && " (principal)"} → {fmtNum(r.msisdn)}</span>{op}</div>;
  if (r.type === "remove") return <span className="font-mono text-xs">{fmtNum(r.target_msisdn)}</span>;
  return <div><span className="font-mono text-xs">{fmtNum(r.msisdn)}</span>{op}</div>;
}

function RequestsTable({ requests, onDone, showUser }) {
  return (
    <ATable head={[...(showUser ? ["Client"] : []), "Type", "Numéro concerné", "Motif", "Statut", "Date", "Actions"]}>
      {requests.map((r) => (
        <tr key={r.id} className="border-t border-slate-800 align-top" data-testid={`pnr-row-${r.id}`}>
          {showUser && <td className="px-3 py-2 text-xs"><p className="font-medium text-slate-200">{r.user_name || "—"}</p><p className="text-slate-400">{r.user_email}</p><p className="font-mono text-[10px] text-slate-500">ID {r.user_id}</p><p className="text-slate-400">Principal : {fmtNum(r.primary_msisdn)}</p></td>}
          <td className="px-3 py-2 text-xs">{TYPE_LABEL[r.type]}</td>
          <td className="px-3 py-2"><NumberCell r={r} /></td>
          <td className="max-w-[220px] px-3 py-2 text-xs text-slate-400">{r.reason || "—"}</td>
          <td className="px-3 py-2 text-xs" data-testid={`pnr-status-${r.id}`}>{REQ_LABEL[r.status]}{r.otp_verified_at && r.status === "verification_required" && <p className="text-emerald-400">OTP validé — en attente d'approbation</p>}</td>
          <td className="px-3 py-2 text-xs text-slate-400">{fmtDate(r.created_at)}</td>
          <td className="px-3 py-2"><RequestActions r={r} onDone={onDone} /></td>
        </tr>
      ))}
      {!requests.length && <tr><td colSpan={7} className="px-3 py-4 text-center text-xs text-slate-500">Aucune demande</td></tr>}
    </ATable>
  );
}

function AddNumber({ uid, onDone }) {
  const [f, setF] = useState({ phone: "", note: "" });
  return (
    <div className="flex flex-wrap gap-2" data-testid="pn-admin-add">
      <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+243970000000" data-testid="pn-admin-add-phone" className={`${inputDark} !w-48`} />
      <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note" data-testid="pn-admin-add-note" className={`${inputDark} !w-48`} />
      <button onClick={() => act(() => api.post(`/admin/payment-numbers/users/${uid}/add`, f), "Numéro ajouté, OTP envoyé au client", onDone)} data-testid="pn-admin-add-submit" className={`${sbtn} bg-accent text-white`}><Plus className="h-3 w-3" />Ajouter et envoyer l'OTP</button>
    </div>
  );
}

function UserDetail({ uid, onClose }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => { api.get(`/admin/payment-numbers/users/${uid}`).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))); }, [uid]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  return (
    <ACard title={`${d.user.name || ""} · ${d.user.email}`} action={<button onClick={onClose} data-testid="pn-user-close"><X className="h-4 w-4 text-slate-400" /></button>}>
      <div className="space-y-4 text-sm" data-testid="pn-user-detail">
        <p className="text-xs text-slate-400">ID {d.user.id} · {d.verified_count}/3 numéros vérifiés · e-mail {d.user.email_verified ? "vérifié" : "non vérifié"} · échecs solde insuffisant (24 h) : {d.nsf_failures_24h}/3{!d.sms_available && " · SMS non configuré"}</p>
        <ATable head={["Numéro", "Principal", "Statut", "Vérifié le", ""]}>
          {d.numbers.map((n) => (
            <tr key={n.id} className="border-t border-slate-800" data-testid={`pn-admin-num-${n.id}`}>
              <td className="px-3 py-2 font-mono text-xs">+{n.msisdn}</td><td className="px-3 py-2 text-xs">{n.is_primary ? "Oui" : ""}</td>
              <td className="px-3 py-2 text-xs">{NUM_LABEL[n.status] || n.status}</td><td className="px-3 py-2 text-xs text-slate-400">{fmtDate(n.verified_at)}</td>
              <td className="px-3 py-2">{n.status === "unverified" && <button onClick={() => act(() => api.post(`/admin/payment-numbers/${n.id}/resend-otp`), "OTP envoyé", load)} data-testid={`pn-admin-resend-${n.id}`} className={`${sbtn} bg-slate-700 text-white`}>Envoyer l'OTP</button>}</td>
            </tr>
          ))}
        </ATable>
        {d.verified_count < 3 && <AddNumber uid={uid} onDone={load} />}
        <div><p className="mb-1 text-xs font-medium text-slate-300">Résultats OTP</p>
          {d.otps.slice(0, 10).map((o) => <p key={o.id} className="text-xs text-slate-400" data-testid={`pn-otp-${o.id}`}>{fmtDate(o.created_at)} · {o.result} · tentatives {o.attempts}</p>)}</div>
        <div><p className="mb-1 text-xs font-medium text-slate-300">Demandes</p><RequestsTable requests={d.requests} onDone={load} /></div>
        <div><p className="mb-1 text-xs font-medium text-slate-300">Historique</p>
          {d.history.map((h) => <p key={h.id} className="text-xs text-slate-400">{fmtDate(h.created_at)} · {h.action} · {h.user_email}</p>)}</div>
      </div>
    </ACard>
  );
}

export default function AdminPaymentNumbers() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [status, setStatus] = useState("open");
  const [rq, setRq] = useState("");
  const loadReqs = useCallback(() => { api.get(`/admin/payment-number-requests?status=${status}&q=${encodeURIComponent(rq)}`).then((r) => setReqs(r.data.requests)).catch((e) => toast.error(apiError(e.response?.data?.detail))); }, [status, rq]);
  useEffect(() => { loadReqs(); }, [status]); // eslint-disable-line react-hooks/exhaustive-deps
  const search = () => api.get(`/admin/payment-numbers/search?q=${encodeURIComponent(q)}`).then((r) => setUsers(r.data.users)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  return (
    <div className="space-y-6" data-testid="admin-payment-numbers">
      <AHeader title="Gestion des numéros de paiement" subtitle="Maximum 3 numéros vérifiés par compte. Un numéro n'est vérifié qu'après un code OTP confirmé par le client." />
      <ACard title="Rechercher un client">
        <div className="flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Identifiant, e-mail ou téléphone" data-testid="pn-search-input" className={inputDark} />
          <button onClick={search} data-testid="pn-search-btn" className={`${sbtn} bg-accent px-4 text-white`}><Search className="h-3.5 w-3.5" />Rechercher</button>
        </div>
        <div className="mt-3 space-y-1">
          {users.map((u) => <button key={u.id} onClick={() => setSel(u.id)} data-testid={`pn-user-${u.id}`} className="block w-full rounded-lg px-3 py-2 text-left text-sm text-slate-200 hover:bg-slate-800">{u.email} · {u.name} · {u.phone || "—"} · {u.verified_count}/3</button>)}
        </div>
      </ACard>
      {sel && <UserDetail uid={sel} onClose={() => setSel(null)} />}
      <ACard title="Demandes des clients">
        <div className="mb-3 flex flex-wrap gap-2">
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="pnr-status-filter" className={`${inputDark} !w-52`}>
            <option value="open">Demandes ouvertes</option><option value="all">Toutes</option>
            {Object.entries(REQ_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input value={rq} onChange={(e) => setRq(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadReqs()} placeholder="Identifiant, e-mail ou téléphone" data-testid="pnr-search-input" className={`${inputDark} !w-72`} />
          <button onClick={loadReqs} data-testid="pnr-search-btn" className={`${sbtn} bg-accent px-4 text-white`}><Search className="h-3.5 w-3.5" />Filtrer</button>
        </div>
        <RequestsTable requests={reqs} onDone={loadReqs} showUser />
      </ACard>
    </div>
  );
}
